import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, Home, User, DollarSign, MapPin, Briefcase, CheckCircle } from 'lucide-react';

const STEPS = ['Personal', 'Financial', 'Employment', 'Property Preferences'];
const CREDIT_RANGES = ['300-499', '500-579', '580-619', '620-659', '660-699', '700-749', '750+'];
const INCOME_RANGES = ['Under $30K', '$30K-$50K', '$50K-$75K', '$75K-$100K', '$100K-$150K', '$150K+'];
const TIMELINES = ['Immediately', '1-3 months', '3-6 months', '6-12 months', '12+ months'];
const PROPERTY_TYPES = [
    { id: 'any', label: 'Any' }, { id: 'single_family', label: 'Single Family' },
    { id: 'townhome', label: 'Townhome' }, { id: 'condo', label: 'Condo' },
    { id: 'multi_family', label: 'Multi-Family' },
];
const EMPLOYMENT_STATUSES = [
    { id: 'employed', label: 'Employed Full-Time' }, { id: 'part_time', label: 'Part-Time' },
    { id: 'self_employed', label: 'Self-Employed' }, { id: 'retired', label: 'Retired' },
    { id: 'unemployed', label: 'Currently Unemployed' },
];

export default function BuyerIntakePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        full_name: user?.name || '', phone: '',
        income_range: '', credit_score_range: '',
        down_payment_available: '', desired_monthly_payment: '',
        target_location: '', timeline_to_purchase: '',
        desired_city: '', desired_state: '', desired_zip: '',
        property_type_pref: 'any', bedrooms_min: 3, bathrooms_min: 2, sqft_min: '',
        employment_status: '', employer_name: '', monthly_income: '',
        years_at_job: '', move_in_timeline: '',
        has_co_borrower: false, bankruptcy_history: false,
        current_rent: '', notes: '',
    });

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const next = () => { if (step < STEPS.length - 1) setStep(step + 1); };
    const prev = () => { if (step > 0) setStep(step - 1); };

    const submit = async () => {
        setLoading(true);
        setError('');
        try {
            await API.post('/buyers/intake', {
                ...form,
                down_payment_available: parseFloat(form.down_payment_available) || 0,
                desired_monthly_payment: parseFloat(form.desired_monthly_payment) || 0,
                monthly_income: parseFloat(form.monthly_income) || 0,
                current_rent: parseFloat(form.current_rent) || 0,
                sqft_min: parseInt(form.sqft_min) || 0,
                years_at_job: parseInt(form.years_at_job) || 0,
            });
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Submission failed');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="buyer-intake-page">
            <Navbar variant="dark" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <h1 className="text-2xl font-medium text-white font-['Outfit'] mb-2">Buyer Application</h1>
                <p className="text-sm text-zinc-500 mb-8">Complete your profile to get matched with owner-financed properties.</p>

                {/* Step indicator */}
                <div className="flex items-center gap-2 mb-8">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex items-center">
                            <div className={`flex items-center gap-1.5 cursor-pointer`} onClick={() => setStep(i)}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-600/20 text-emerald-400 ring-2 ring-emerald-600' : 'bg-white/5 text-zinc-500'}`}>
                                    {i < step ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                                </div>
                                <span className="text-xs text-zinc-400 hidden sm:inline">{s}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-0.5 mx-2 ${i < step ? 'bg-emerald-600' : 'bg-white/10'}`} />}
                        </div>
                    ))}
                </div>

                {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8">
                    {/* Step 0: Personal */}
                    {step === 0 && (
                        <div className="space-y-4" data-testid="buyer-step-personal">
                            <div className="flex items-center gap-2 mb-4"><User className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Personal Information</h2></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 sm:col-span-1">
                                    <Label className="text-zinc-400 text-sm">Full Name</Label>
                                    <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="buyer-name"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <Label className="text-zinc-400 text-sm">Phone</Label>
                                    <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(713) 555-0100" data-testid="buyer-phone"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Credit Score Range</Label>
                                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mt-1">
                                    {CREDIT_RANGES.map(r => (
                                        <button key={r} onClick={() => set('credit_score_range', r)} data-testid={`credit-${r}`}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.credit_score_range === r ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">How soon do you want to purchase?</Label>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-1">
                                    {TIMELINES.map(t => (
                                        <button key={t} onClick={() => set('timeline_to_purchase', t)}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.timeline_to_purchase === t ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-4 mt-4">
                                <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                                    <input type="checkbox" checked={form.has_co_borrower} onChange={e => set('has_co_borrower', e.target.checked)} className="rounded border-white/20" />
                                    I have a co-borrower
                                </label>
                                <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                                    <input type="checkbox" checked={form.bankruptcy_history} onChange={e => set('bankruptcy_history', e.target.checked)} className="rounded border-white/20" />
                                    Prior bankruptcy
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Financial */}
                    {step === 1 && (
                        <div className="space-y-4" data-testid="buyer-step-financial">
                            <div className="flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Financial Information</h2></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Annual Income Range</Label>
                                    <select value={form.income_range} onChange={e => set('income_range', e.target.value)} data-testid="buyer-income"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mt-1">
                                        <option value="">Select...</option>
                                        {INCOME_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Monthly Income ($)</Label>
                                    <Input type="number" value={form.monthly_income} onChange={e => set('monthly_income', e.target.value)} data-testid="buyer-monthly-income"
                                        placeholder="5000" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Down Payment Available ($)</Label>
                                    <Input type="number" value={form.down_payment_available} onChange={e => set('down_payment_available', e.target.value)} data-testid="buyer-down-payment"
                                        placeholder="10000" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Desired Monthly Payment ($)</Label>
                                    <Input type="number" value={form.desired_monthly_payment} onChange={e => set('desired_monthly_payment', e.target.value)} data-testid="buyer-monthly-payment"
                                        placeholder="1500" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Current Monthly Rent ($)</Label>
                                    <Input type="number" value={form.current_rent} onChange={e => set('current_rent', e.target.value)}
                                        placeholder="1200" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Employment */}
                    {step === 2 && (
                        <div className="space-y-4" data-testid="buyer-step-employment">
                            <div className="flex items-center gap-2 mb-4"><Briefcase className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Employment</h2></div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Employment Status</Label>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-1">
                                    {EMPLOYMENT_STATUSES.map(s => (
                                        <button key={s.id} onClick={() => set('employment_status', s.id)}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.employment_status === s.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Employer Name</Label>
                                    <Input value={form.employer_name} onChange={e => set('employer_name', e.target.value)}
                                        placeholder="Company name" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Years at Current Job</Label>
                                    <Input type="number" value={form.years_at_job} onChange={e => set('years_at_job', e.target.value)}
                                        placeholder="2" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Property Preferences */}
                    {step === 3 && (
                        <div className="space-y-4" data-testid="buyer-step-property">
                            <div className="flex items-center gap-2 mb-4"><Home className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Property Preferences</h2></div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Desired City</Label>
                                    <Input value={form.desired_city} onChange={e => set('desired_city', e.target.value)} data-testid="buyer-city"
                                        placeholder="Houston" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">State</Label>
                                    <Input value={form.desired_state} onChange={e => set('desired_state', e.target.value)} data-testid="buyer-state"
                                        placeholder="TX" maxLength={2} className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                                    <Input value={form.desired_zip} onChange={e => set('desired_zip', e.target.value)} data-testid="buyer-zip"
                                        placeholder="77001" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Property Type</Label>
                                <div className="grid grid-cols-5 gap-2 mt-1">
                                    {PROPERTY_TYPES.map(t => (
                                        <button key={t.id} onClick={() => set('property_type_pref', t.id)}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.property_type_pref === t.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Min Bedrooms</Label>
                                    <Input type="number" value={form.bedrooms_min} onChange={e => set('bedrooms_min', parseInt(e.target.value) || 0)} data-testid="buyer-beds"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Min Bathrooms</Label>
                                    <Input type="number" value={form.bathrooms_min} onChange={e => set('bathrooms_min', parseInt(e.target.value) || 0)} data-testid="buyer-baths"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Min Sq Ft</Label>
                                    <Input type="number" value={form.sqft_min} onChange={e => set('sqft_min', e.target.value)} data-testid="buyer-sqft"
                                        placeholder="1200" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Move-in Timeline</Label>
                                <Input value={form.move_in_timeline} onChange={e => set('move_in_timeline', e.target.value)}
                                    placeholder="e.g., As soon as possible, After lease ends in June" className="bg-white/5 border-white/10 text-white mt-1" />
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">General Target Location / Neighborhoods</Label>
                                <Input value={form.target_location} onChange={e => set('target_location', e.target.value)} data-testid="buyer-location"
                                    placeholder="e.g., Northwest Houston, Katy, Sugar Land" className="bg-white/5 border-white/10 text-white mt-1" />
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Additional Notes</Label>
                                <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                                    placeholder="Anything else we should know..." className="bg-white/5 border-white/10 text-white mt-1 min-h-16" />
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                        <Button onClick={prev} disabled={step === 0} variant="ghost" className="text-zinc-400 hover:text-white">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                        {step < STEPS.length - 1 ? (
                            <Button onClick={next} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="buyer-next-btn">
                                Next <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="buyer-submit-btn">
                                {loading ? 'Submitting...' : 'Submit Application'} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
